import { useState, useEffect } from 'react'
import { DetailHeader } from '@/components/DetailHeader'
import { ImageGallery } from '@/components/ImageGallery'
import { PlanDetailSummary } from '@/components/PlanDetailSummary'
import { MatchingUnitsPanel } from '@/components/UnitListButton'
import type { UnitListItem, UnitListButtonPricing } from '@/components/UnitListButton'
import { Tag } from '@/components/Tag'
import { DetailTextSection } from '@/components/DetailTextSection'
import { DetailTable } from '@/components/DetailTable'
import type { DetailTableRow } from '@/components/DetailTable'
import { MapPlaceholder, MAP_HEIGHT_CLASS } from '@/components/MapPlaceholder'
import { Button } from '@/components/Button'
import { Link } from '@/components/Link'
import { NoResultsIllustration } from '@/components/NoResultsIllustration/NoResultsIllustration'
import { Disclaimer } from '@/components/Disclaimer'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { fetchPlanDetail, ApiError } from '@/api/fetchCollection'
import { getOptions, getLabels, getConfig } from '@/data/config'
import { emitPlanDetailsClick } from '@/metrics/metrics'
import { formatAreaDisplay, formatMatchingUnitsLabel, hasLeaseTerm, resolvePricingDisplay, resolveUnitAvailability, unitLabelForCount } from '@/data/mappers'
import { formatAvailableDate } from '@/utils/formatDate'
import { isUnitOnNotice, onNoticeTagLabel } from '@/utils/onNotice'
import type { ApiPlanDetail, ApiUnit } from '@/types/api'

interface PlanDetailProps {
	planId: string
	onBack: () => void
	onShare: () => void
	onGallery: (images: Array<{ src: string; caption?: string }>, index: number, title?: string) => void
	onTour: (url: string, title: string) => void
	onCalculator: (url: string) => void
	onUnitClick: (unitId: string) => void
	mockPlan?: ApiPlanDetail | null
	mockUnits?: ApiUnit[]
}

export function PlanDetail ({
	planId,
	onBack,
	onShare,
	onGallery,
	onTour,
	onCalculator,
	onUnitClick,
	mockPlan,
	mockUnits
}: PlanDetailProps) {
	const [plan, setPlan] = useState<ApiPlanDetail | null>(null)
	const [units, setUnits] = useState<ApiUnit[]>([])
	const [loading, setLoading] = useState(true)
	const [notFound, setNotFound] = useState(false)
	const [loadError, setLoadError] = useState(false)
	const [reloadKey, setReloadKey] = useState(0)
	const [galleryIndex, setGalleryIndex] = useState(0)
	const isDesktop = useMediaQuery('(min-width: 768px)')

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		setNotFound(false)
		setLoadError(false)
		setPlan(null)
		setUnits([])
		setGalleryIndex(0)

		if (mockPlan !== undefined) {
			if (mockPlan) {
				setPlan(mockPlan)
				setUnits(mockUnits ?? [])
			} else {
				setNotFound(true)
			}
			setLoading(false)
			return
		}

		fetchPlanDetail(planId)
			.then(res => {
				if (!cancelled) {
					setPlan(res.data)
					setUnits(res.units ?? [])
					setLoading(false)
				}
			})
			.catch(err => {
				if (!cancelled) {
					if (err instanceof ApiError && err.status === 404) setNotFound(true)
					else setLoadError(true)
					setLoading(false)
				}
			})

		return () => { cancelled = true }
	}, [planId, mockPlan, mockUnits, reloadKey])

	// Fire the details.click metric once the plan detail view has loaded.
	useEffect(() => {
		if (plan) emitPlanDetailsClick(plan)
	}, [plan])

	const opts = getOptions()
	const labels = getLabels()

	if (loading) {
		return (
			<div className='spaces-app spaces-plan-detail spaces:bg-canvas-app spaces:min-h-screen'>
				<div className='spaces:flex spaces:items-center spaces:justify-center spaces:py-80'>
					<span className='spaces:text-16/24 spaces:font-default spaces:text-content-3'>Loading…</span>
				</div>
			</div>
		)
	}

	if (loadError || notFound || !plan) {
		return (
			<div className='spaces-app spaces-plan-detail spaces:bg-canvas-app spaces:min-h-screen'>
				<Disclaimer variant='top' text={labels.disclaimerTop} className='spaces:mb-16' />
				<div className='spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-24 spaces:overflow-hidden'>
					<DetailHeader
						backLabel={isDesktop ? 'Back to Search' : 'Back'}
						onBack={onBack}
					/>
					<div
						role={loadError ? 'alert' : undefined}
						className={`${loadError ? 'spaces-plan-load-error' : 'spaces-plan-not-found'} spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-16 spaces:py-80 spaces:px-16`}
					>
						<NoResultsIllustration />
						<h2 className='spaces:text-18/24 spaces:font-semi spaces:text-content-1'>
							{loadError ? 'Something went wrong loading this floor plan.' : 'Floor plan is no longer available.'}
						</h2>
						<p className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>
							{loadError ? 'Please check your connection and try again.' : 'Return to search to see other available options.'}
						</p>
						<div className='spaces:flex spaces:flex-wrap spaces:justify-center spaces:gap-8'>
							{loadError && (
								<Button
									appearance='solid'
									color='brand'
									size='md'
									label='Retry'
									spacesClass='spaces-retry'
									onClick={() => { setReloadKey(k => k + 1) }}
								/>
							)}
							<Button
								appearance={loadError ? 'ghost' : 'solid'}
								color={loadError ? 'content2' : 'brand'}
								size='md'
								label='Back to Search'
								onClick={onBack}
							/>
						</div>
					</div>
				</div>
				<Disclaimer variant='bottom' text={labels.disclaimer} link={labels.disclaimerLink} className='spaces:mt-16' />
			</div>
		)
	}

	// Build gallery images
	const galleryImages = buildGalleryImages(plan)
	const galleryImageSrcs = galleryImages.map(img => img.src)

	// Urgency message — show when < 4 available units and plan is available
	const urgencyMessage = plan.is_available && plan.available_unit_count > 0 && plan.available_unit_count < 4
		? `Only ${plan.available_unit_count} ${unitLabelForCount(plan.available_unit_count, labels).toLowerCase()} left!`
		: undefined

	// Special message — show if any unit has specials
	const hasUnitSpecials = units.some(u => !!u.specials)
	const specialMessage = hasUnitSpecials ? 'Special available on select units' : undefined

	// Virtual tour
	const tourUrl = plan.tour_url || null
	const hasVirtualTour = !!tourUrl

	// Amenities
	const planAmenities = (Array.isArray(plan.amenities) ? plan.amenities : []).map(a => a.amenity_label)

	// Tags
	const planTags = (Array.isArray(plan.tags) ? plan.tags : []).map(t => t.name)

	// Pricing logic for matching units
	const priceIsHidden = plan.price_hidden || opts.hidePa || opts.hideStartingAt || isPriceHidden(plan.min_price)

	const isRevManagement = opts.useRevManagement && opts.applyProvider !== 'None'

	let unitPricing: UnitListButtonPricing = 'flat'
	let unitHasFees = true

	if (priceIsHidden) {
		unitPricing = 'hidden'
	} else if (isRevManagement) {
		unitPricing = 'revenue-management'
	}

	// Unit rows follow the plan's mode so the fees flag and the row prices agree.
	const pricingMode = resolvePricingDisplay(plan.pricing_display)

	if (!priceIsHidden) {
		if (pricingMode !== 'total' && pricingMode !== 'both') {
			unitHasFees = false
		}
	}

	// Disclaimer symbol
	const hasDisclaimerSymbol = !priceIsHidden && labels.unitPricingPostText.includes('*')

	// Calculator enabled
	const calculatorEnabled = opts.enableSightmapCalculator && !!getConfig().sightmapEmbedUrl

	// SightMap unit IDs — parse CSV from sightmap_filter (empty array when no availability)
	const sightmapUnitIds = plan.sightmap_filter
		? plan.sightmap_filter.split(',')
		: []

	// Map units to UnitListItem props
	const unitListItems: UnitListItem[] = units
		.filter(u => u.is_available)
		.map(u => {
			const isOnNotice = isUnitOnNotice(u)
			const noLeaseTerm = !hasLeaseTerm(u.default_lease_term)
			const unitPriceHidden = isOnNotice || priceIsHidden ||
				u.price_hidden ||
				isPriceHidden(u.price) ||
				noLeaseTerm

			let priceRange: string | undefined
			let baseRent: string | undefined

			if (!unitPriceHidden) {
				if (pricingMode === 'total' || pricingMode === 'both') {
					priceRange = u.price_with_fees_string
					if (pricingMode === 'both') {
						baseRent = u.price_string
					}
				} else {
					baseRent = u.price_string
				}
			}

			const { availableDate, availabilityOverride } = resolveUnitAvailability(u, isOnNotice)

			const unitTags = (Array.isArray(u.tags) ? u.tags : []).map(t => t.name)

			return {
				unitNumber: u.unit_number,
				// Floor is intentionally omitted — floor_id is a raw SightMap ID, not a
				// human floor label, so it must not be surfaced in the UI.
				availableDate,
				availabilityOverride,
				priceRange,
				baseRent,
				leaseTerm: unitPriceHidden ? undefined : (u.default_lease_term ? `${u.default_lease_term} Mo.` : undefined),
				hasSpecial: !!u.specials,
				tags: unitTags.length > 0 ? unitTags : undefined,
				onNoticeTag: isOnNotice ? onNoticeTagLabel(u) : undefined,
				pricing: isOnNotice || u.price_hidden || noLeaseTerm ? 'hidden' as const : undefined
			}
		})

	return (
		<div className='spaces-app spaces-plan-detail spaces:bg-canvas-app spaces:min-h-screen'>
			<Disclaimer variant='top' text={labels.disclaimerTop} className='spaces:mb-16' />

			<div className='spaces:flex spaces:flex-col spaces:md:flex-row spaces:gap-16 spaces:items-stretch spaces:md:items-start'>
				{/* Left panel — max-width matches the ResultsLayout split list column; flexbox gives the leftover width to the map. */}
				<div className='spaces:flex-1 spaces:min-w-0 spaces:max-w-[720px] spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-24 spaces:overflow-hidden'>
					<DetailHeader
						backLabel={isDesktop ? 'Back to Search' : 'Back'}
						onBack={onBack}
						onShare={onShare}
					/>

					<div className='spaces:flex spaces:flex-col spaces:gap-24 spaces:p-16 spaces:md:p-24'>
						{/* Visuals */}
						{galleryImageSrcs.length > 0 && (
							<ImageGallery
								images={galleryImageSrcs}
								label={plan.name}
								currentIndex={galleryIndex}
								onIndexChange={setGalleryIndex}
								onOpen={() => { onGallery(galleryImages, galleryIndex, plan?.name) }}
								hasVirtualTour={hasVirtualTour}
								onVirtualTour={() => { if (tourUrl) onTour(tourUrl, plan.name) }}
							/>
						)}

						{/* Summary */}
						<PlanDetailSummary
							planName={plan.name}
							beds={plan.bedroom_count_string_short}
							baths={plan.bathroom_count_string_short}
							sqftRange={plan.is_available ? formatAreaDisplay(plan.area.display) : undefined}
							urgencyMessage={urgencyMessage}
							specialMessage={specialMessage}
						/>

						{/* No availability */}
						{!plan.is_available && (
							<div className='spaces-plan-no-availability spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:shadow-sm-below spaces:overflow-hidden spaces:p-3'>
								<div className='spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-24 spaces:px-16 spaces:py-40 spaces:md:p-40'>
									<NoResultsIllustration />
									<div className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4 spaces:w-full spaces:text-center'>
										<p className='spaces:text-18/24 spaces:font-default spaces:text-content-1'>
											No units are available for this plan.
										</p>
										<p className='spaces:text-16/24 spaces:font-default spaces:text-content-3'>
											Check back later or{' '}
											{opts.contactLink?.url
												? (
													<Link
														spacesClass='spaces-contact-cta'
														href={opts.contactLink.url}
														target={opts.contactLink.target || undefined}
														label='contact our team'
													/>
													)
												: 'contact our team'}{' '}
											for more info.
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Matching units */}
						{plan.is_available && unitListItems.length > 0 && (
							<MatchingUnitsPanel
								units={unitListItems}
								label={formatMatchingUnitsLabel(unitListItems.length, labels)}
								pricing={unitPricing}
								hasFees={unitHasFees}
								hasDisclaimerSymbol={hasDisclaimerSymbol}
								onUnitClick={unitNumber => {
									const matchedUnit = units.find(u => u.unit_number === unitNumber)
									if (matchedUnit) onUnitClick(String(matchedUnit.id))
								}}
								onPricingClick={calculatorEnabled ? unitNumber => {
									const matchedUnit = units.find(u => u.unit_number === unitNumber)
									if (!matchedUnit) return
									const sightmapEmbedUrl = getConfig().sightmapEmbedUrl
									const params = new URLSearchParams()
									params.set('unit_number', matchedUnit.unit_number)
									params.set('lease_term', matchedUnit.default_lease_term?.toString() ?? '')
									params.set('move_in_date', matchedUnit.available_on ?? '')
									params.set('mode', 'expense_calculator')
									onCalculator(`${sightmapEmbedUrl}?${params.toString()}`)
								} : undefined}
							/>
						)}

						{/* Sub-details */}
						{(() => {
							const subDetailRows: DetailTableRow[] = []
							if (planTags.length > 0) {
								subDetailRows.push({
									label: 'Tags',
									valueClassName: 'spaces:flex spaces:flex-wrap spaces:gap-8 spaces:items-start spaces:p-8',
									value: (
										<>
											{planTags.map(t => (
												<Tag key={t} label={t} color='brand' appearance='subtle' size='md' />
											))}
										</>
									)
								})
							}
							subDetailRows.push({
								label: 'Bedrooms',
								value: plan.bedroom_count_string_short
							})
							subDetailRows.push({
								label: 'Bathrooms',
								value: plan.bathroom_count_string_short
							})
							if (plan.is_available) {
								subDetailRows.push({
									label: 'Area',
									value: (
										<span className='spaces:text-16/24 spaces:font-default spaces:text-content-1'>
											{formatAreaDisplay(plan.area.display)} ft<sup className='spaces:text-10 spaces:font-strong'>2</sup>
										</span>
									)
								})
							}
							if (plan.soonest_available) {
								subDetailRows.push({
									label: 'Available',
									value: formatAvailableDate(plan.soonest_available)
								})
							}
							return (
								<div className='spaces-plan-sub-details'>
									<DetailTable rows={subDetailRows} />
								</div>
							)
						})()}

						{/* Overview */}
						{plan.description && (
							<DetailTextSection title='Overview' content={plan.description} />
						)}

						{/* Divider between Overview and Features */}
						{plan.description && planAmenities.length > 0 && (
							<div className='spaces:h-px spaces:bg-canvas-3' />
						)}

						{/* Features */}
						{planAmenities.length > 0 && (
							<div className='spaces-plan-detail-amenities'>
								<DetailTextSection title='Features' listItems={planAmenities} />
							</div>
						)}

						{!isDesktop && <MapPlaceholder sightmapUnitIds={sightmapUnitIds} onUnitClick={onUnitClick} mockUnits={mockUnits} className='spaces:h-[436px]' />}
					</div>
				</div>

				{/* Right panel — map */}
				<div className={`spaces:hidden spaces:md:block spaces:flex-1 spaces:min-w-[400px] spaces:sticky spaces:top-16 ${MAP_HEIGHT_CLASS}`}>
					<MapPlaceholder sightmapUnitIds={sightmapUnitIds} onUnitClick={onUnitClick} mockUnits={mockUnits} className='spaces:h-full' />
				</div>
			</div>

			<Disclaimer variant='bottom' text={labels.disclaimer} link={labels.disclaimerLink} className='spaces:mt-16' />
		</div>
	)
}

/* ---------- Helpers ---------- */

const FPO_IMAGE = '/wp-content/plugins/ecs-spaces/public/images/spaces-fpo.png'

function isPriceHidden (price: number | null, priceString?: string): boolean {
	if (price === null || price === 0) return true
	if (priceString === 'Call for pricing') return true
	return false
}

function buildGalleryImages (plan: ApiPlanDetail): Array<{ src: string; caption?: string }> {
	const images: Array<{ src: string; caption?: string }> = []

	// Primary image
	const primary = plan.primary_image_cdn ?? FPO_IMAGE
	images.push({ src: primary, caption: plan.primary_image_caption ?? undefined })

	// Alternate image
	if (plan.alternate_image_cdn) {
		images.push({ src: plan.alternate_image_cdn, caption: plan.alternate_image_caption ?? undefined })
	}

	// Gallery images
	if (plan.gallery && Array.isArray(plan.gallery)) {
		for (const img of plan.gallery) {
			if (typeof img === 'string') {
				images.push({ src: img })
			} else if (img && typeof img === 'object' && 'src' in img) {
				images.push({ src: String((img as { src: string }).src) })
			}
		}
	}

	return images
}
